/**
 * Cadena del criterio de un documento (DESIGN.md §11.7): contrato de `GET /api/documents/:id/cadena`. Solo citas
 * resueltas a documentos de la base; las no resueltas no llegan. Mientras el backend no publique la ruta, el arnés y
 * los tests la alimentan con `fixtures/cadena-2723.json` (misma forma).
 */
import { numeroReal, tipoSingular } from './bibliotecas';

export interface DocCitado {
  id: string;
  document_id: string;
  tipo: string;
  numero: string;
  year: number;
  fecha_publicacion: string | null;
  titulo: string;
  /** La frase exacta del documento que cita, la que lleva la cita. */
  frase: string;
  /** La cita va en una nota al pie. */
  en_nota: boolean;
  /** Número de la nota, si `en_nota`. */
  nota: number | null;
  /** Párrafo (¶) del documento que cita donde está la frase. */
  parrafo: number | null;
  /** Verbo de tratamiento si el texto lo trae (confirma, reitera, precisa, complementa, modifica, deja sin efecto, reconsidera). */
  tratamiento: string | null;
}

export interface Modificacion {
  id: string;
  document_id: string;
  tipo: string;
  numero: string;
  year: number;
  fecha_publicacion: string | null;
  frase: string | null;
}

export interface NormaCabecera {
  /** «LIR art. 17 N° 9». */
  etiqueta: string;
  ley: string;
  articulo: string;
  numero: string | null;
  /** Documentos que mostrará la Biblioteca con ese filtro; null si la ley no está en su catálogo (no se muestra conteo). */
  total: number | null;
  /** Filtro de la Biblioteca para ese par. */
  filtro: { ley: string; articulo: string };
}

export interface Cadena {
  vigencia: { modificado_por: Modificacion | null };
  normas: NormaCabecera[];
  citado_por: DocCitado[];
  cita_a: DocCitado[];
  original_url: string | null;
}

/** «Oficio 3599», «Circular 17»: el tipo en singular y el número sin ceros a la izquierda. */
export function etiquetaCorta(d: Pick<DocCitado, 'tipo' | 'numero'>): string {
  // La base guarda «0110»; en pantalla va «110» (maqueta m12).
  const n = numeroReal(d.numero).replace(/^0+(?=\d)/, '');
  return `${tipoSingular(d.tipo) || 'Documento'}${n ? ` ${n}` : ''}`;
}

/** «Modificado por el Oficio 2679 de 2026». */
export function rotuloModificacion(m: Modificacion): string {
  return `Modificado por el ${etiquetaCorta(m)}${m.year ? ` de ${m.year}` : ''}`;
}

/** «4 oficios posteriores», «1 oficio posterior», «3 documentos». */
export function cuentaCitas(docs: DocCitado[], posteriores: boolean): string {
  const n = docs.length;
  const todosOficios = docs.every(d => (d.tipo || '').toLowerCase() === 'oficio');
  const nombre = todosOficios ? (n === 1 ? 'oficio' : 'oficios') : (n === 1 ? 'documento' : 'documentos');
  return `${n} ${nombre}${posteriores ? (n === 1 ? ' posterior' : ' posteriores') : ''}`;
}

/** Enlace de la Biblioteca administrativa filtrada por la norma de la cabecera. */
export function enlaceNorma(n: NormaCabecera): string {
  const p = new URLSearchParams();
  p.set('ley', n.filtro.ley);
  if (n.filtro.articulo) p.set('art', n.filtro.articulo);
  return `/biblioteca/adm?${p.toString()}`;
}

/**
 * Qué hace un documento posterior con el criterio de este (vocabulario confirmado por el dueño el 03-11):
 * Corrobora (confirma o reitera) · Deriva (precisa, acota o complementa) · Cambia (modifica o deja sin efecto) · Se mantiene
 * (lo cita o lo aplica sin cambiarlo). «Difiere» no tiene método todavía y no se muestra.
 */
export type Criterio = 'Corrobora' | 'Deriva' | 'Cambia' | 'Se mantiene';

const POR_TRATAMIENTO: Record<string, Criterio> = {
  confirma: 'Corrobora', reitera: 'Corrobora',
  precisa: 'Deriva', complementa: 'Deriva',
  modifica: 'Cambia', 'deja sin efecto': 'Cambia', reconsidera: 'Cambia',
};

export function criterioDe(tratamiento: string | null | undefined): Criterio {
  return (tratamiento && POR_TRATAMIENTO[tratamiento]) || 'Se mantiene';
}

export interface FilaEvolucion { doc: DocCitado; criterio: Criterio }

const tiempo = (f: string | null) => (f ? Date.parse(f) : Infinity);

/**
 * Subpestaña «Evolución»: los documentos posteriores que se refieren a este, en orden cronológico, con su criterio. El que
 * lo modifica según `documentos_sin_efecto` (vigencia) entra como «Cambia» aunque no lo cite por número. `cambio` es el año del
 * primer cambio, para la línea de estado.
 */
export function evolucion(c: Cadena): { filas: FilaEvolucion[]; cambio: number | null } {
  const filas: FilaEvolucion[] = c.citado_por.map(doc => ({ doc, criterio: criterioDe(doc.tratamiento) }));
  const mod = c.vigencia.modificado_por;
  if (mod) {
    const ya = filas.find(f => f.doc.id === mod.id);
    if (ya) ya.criterio = 'Cambia';
    else filas.push({ doc: { ...mod, titulo: '', frase: mod.frase || '', en_nota: false, nota: null, parrafo: null, tratamiento: 'deja sin efecto' }, criterio: 'Cambia' });
  }
  filas.sort((a, b) => tiempo(a.doc.fecha_publicacion) - tiempo(b.doc.fecha_publicacion));
  const primero = filas.find(f => f.criterio === 'Cambia');
  return { filas, cambio: primero ? (primero.doc.year || null) : null };
}
